import { ProjectStatus } from '@/contracts/enums/ProjectStatus';
import {
  LoadProjectSummariesRequest,
  StoreProjectRequest,
} from '@/contracts/requests/projectRequests';
import { LoadProjectResponse } from '@/contracts/responses/LoadProjectResponse';
import {
  LoadProjectSummariesResponse,
  ProjectSummary,
  StoreProjectResponse,
} from '@/contracts/responses/projectResponses';
import { SurveyInProject } from '@/features/project/components/SurveysCard';
import { loggingUtility } from '@/utils/loggingUtility';
import { isOrgSubdomain } from '@/utils/subdomainUtils';
import { apiClient } from './clients/axiosClient';

/**
 * Load project summaries with filtering, sorting, and pagination
 * Calls the backend API to retrieve projects for the organization
 */
export const loadProjectSummaries = async (
  filters?: LoadProjectSummariesRequest
): Promise<LoadProjectSummariesResponse> => {
  try {
    // Call the backend API
    const response = await apiClient.get<{
      projects: Array<{
        projectId: string;
        projectName: string;
        projectStatus: ProjectStatus;
        numberOfSurveysCompleted: number;
        createdAt: string;
      }>;
    }>('/surveys/projects');

    // Map backend response to frontend format
    let mappedProjects: ProjectSummary[] = response.projects.map(project => ({
      projectId: project.projectId,
      projectName: project.projectName,
      projectStatus: project.projectStatus,
      numberOfSurveys: project.numberOfSurveysCompleted,
      createdAt: project.createdAt,
    }));

    // Keep a copy of all projects before filtering for count calculations
    const allProjects = [...mappedProjects];

    // Filter by access type
    if (filters?.accessTypes && filters.accessTypes.length > 0) {
      mappedProjects = mappedProjects.filter(project =>
        filters.accessTypes!.includes(project.projectStatus)
      );
    }

    // Sort projects (client-side for now since backend doesn't support it)
    if (filters?.sortBy) {
      mappedProjects.sort((a, b) => {
        const sortBy = filters.sortBy!;
        const sortOrder = filters.sortOrder || 'asc';

        let comparison = 0;
        if (sortBy === 'projectName') {
          comparison = a.projectName.localeCompare(b.projectName);
        } else if (sortBy === 'numberOfSurveys') {
          comparison = a.numberOfSurveys - b.numberOfSurveys;
        } else if (sortBy === 'createdAt') {
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        }

        return sortOrder === 'asc' ? comparison : -comparison;
      });
    }

    // Calculate counts by status
    const publicCount = allProjects.filter(
      p => p.projectStatus === ProjectStatus.Public
    ).length;
    const privateCount = allProjects.filter(
      p => p.projectStatus === ProjectStatus.Private
    ).length;
    const archivedCount = allProjects.filter(
      p => p.projectStatus === ProjectStatus.Archived
    ).length;

    const countByAccessType = {
      all: publicCount + privateCount, // 'all' tab excludes archived
      public: publicCount,
      private: privateCount,
      archived: archivedCount,
    };

    // Pagination (client-side for now since backend doesn't support it)
    const page = filters?.page || 1;
    const pageSize = filters?.pageSize || 20;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedProjects = mappedProjects.slice(startIndex, endIndex);

    const result: LoadProjectSummariesResponse = {
      projects: paginatedProjects,
      countByAccessType,
      page,
      pageSize,
      totalCount: mappedProjects.length,
    };

    return result;
  } catch (error) {
    loggingUtility.error('Error loading project summaries:', error);
    throw error;
  }
};

/**
 * Get a single project by ID
 * Calls the backend API to retrieve project details
 */
export const getProject = async (
  projectId: string
): Promise<LoadProjectResponse> => {
  try {
    // Use public endpoint if on organization subdomain (public landing page), otherwise use authenticated endpoint
    // Organization subdomains are public landing pages, app subdomain is authenticated interface
    const isPublicLandingPage = isOrgSubdomain();

    const endpoint = isPublicLandingPage
      ? `/feedback/projects/${projectId}`
      : `/surveys/projects/${projectId}`;

    const response = await apiClient.get<LoadProjectResponse>(endpoint);

    if (!response.project) {
      throw new Error(`Project not found: ${projectId}`);
    }

    return response;
  } catch (error) {
    loggingUtility.error('Error getting project:', error);
    throw error;
  }
};

/**
 * Helper function to format date from ISO string to YYYY-MM-DD or empty string
 */
const formatDateForSurveyInProject = (
  date: string | null | undefined
): string => {
  if (!date) return '';
  try {
    return new Date(date).toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Helper function to map status - filters out archived and maps generating to draft
 */
const mapStatusForSurveyInProject = (
  status: string
): 'draft' | 'scheduled' | 'published' | 'completed' => {
  const statusLower = status.toLowerCase();
  if (
    statusLower === 'draft' ||
    statusLower === 'scheduled' ||
    statusLower === 'published' ||
    statusLower === 'completed'
  ) {
    return statusLower as 'draft' | 'scheduled' | 'published' | 'completed';
  }
  // Handle 'generating' or any unknown status - default to 'draft'
  return 'draft';
};

/**
 * Get surveys for a project
 * Backend returns SurveySummaryDto (filtered to exclude archived), frontend formats dates
 */
export const getProjectSurveys = async (
  projectId: string
): Promise<SurveyInProject[]> => {
  try {
    // Call the backend API - backend returns SurveySummaryDto (archived filtered out)
    const response = await apiClient.get<{
      project: {
        id: string;
        [key: string]: unknown;
      } | null;
      surveys: Array<{
        surveyId: string;
        title: string;
        status: string;
        startDate: string | null;
        endDate: string | null;
        mixerQuestionId?: string | null;
      }>;
      mixers: unknown[];
      votes: unknown[];
    }>(`/surveys/projects/${projectId}`);

    if (!response.project) {
      throw new Error(`Project not found: ${projectId}`);
    }

    // Format dates and map status for frontend SurveyInProject format
    const formattedSurveys: SurveyInProject[] = response.surveys.map(
      survey => ({
        surveyId: survey.surveyId,
        title: survey.title,
        status: mapStatusForSurveyInProject(survey.status),
        startDate: formatDateForSurveyInProject(survey.startDate),
        endDate: formatDateForSurveyInProject(survey.endDate),
        mixerQuestionId: survey.mixerQuestionId || null,
      })
    );

    return formattedSurveys;
  } catch (error) {
    loggingUtility.error('Error getting project surveys:', error);
    throw error;
  }
};

/**
 * Store a project (create or update)
 * Maps frontend project data to backend format and calls the API
 */
export const storeProject = async (
  projectData: StoreProjectRequest
): Promise<StoreProjectResponse> => {
  try {
    // Map frontend project data to backend format
    // Backend accepts all project detail fields
    // organizationId is extracted from JWT cookie on backend
    // Note: Delta properties (projectDescriptionDelta, textBlockContentDelta) are intentionally
    // set to null on save. Tiptap HTML is the source of truth, and deltas are only used for
    // backward compatibility when loading legacy Quill content.
    const backendProjectData = {
      id: projectData.projectId,
      name: projectData.projectName,
      status: projectData.status || ProjectStatus.Public,
      projectDescription: projectData.projectDescription,
      projectDescriptionDelta: null, // Clear delta - Tiptap HTML is source of truth
      profileImage: projectData.profileImage,
      profileImageAltText: projectData.profileImageAltText,
      headerImage: projectData.headerImage,
      headerImageAltText: projectData.headerImageAltText,
      resourceLinks: projectData.resourceLinks,
      landingPageUrl: projectData.landingPageUrl,
      displayMixerQuestion: projectData.displayMixerQuestion,
      mixerSurveyId: projectData.mixerSurveyId,
      isLandingPageActive: projectData.isLandingPageActive,
      textBlockTitle: projectData.textBlockTitle,
      textBlockContent: projectData.textBlockContent,
      textBlockContentDelta: null, // Clear delta - Tiptap HTML is source of truth
      // Always include surveyIds (even if empty array) so backend can manage relationships
      surveyIds: projectData.surveyIds || [],
    };

    const response = await apiClient.post<{
      projectId: string;
      headerImage?: string | null;
      profileImage?: string | null;
    }>('/surveys/projects/store', backendProjectData);

    return {
      projectId: response.projectId,
      headerImage: response.headerImage,
      profileImage: response.profileImage,
    };
  } catch (error) {
    loggingUtility.error('Error storing project:', error);
    throw error;
  }
};

/**
 * Duplicate a project
 * Loads the existing project and creates a new one with all the same data but a new name
 */
export const duplicateProject = async (
  projectId: string,
  newProjectName: string
): Promise<StoreProjectResponse> => {
  try {
    // Load the existing project to get all its data
    const existingProjectResponse = await getProject(projectId);
    const existingProject = existingProjectResponse.project!;

    // Create a new project with all the same data but the new name
    // Note: Delta properties are not included - they will be cleared on save
    const duplicateProjectData: StoreProjectRequest = {
      projectName: newProjectName,
      landingPageUrl: '', // Will be auto-generated by backend
      isLandingPageActive: existingProject.isLandingPageActive ?? true,
      status: ProjectStatus.Public,
      profileImage: existingProject.profileImage,
      profileImageAltText: existingProject.profileImageAltText,
      headerImage: existingProject.headerImage,
      headerImageAltText: existingProject.headerImageAltText,
      projectDescription: existingProject.projectDescription ?? '',
      textBlockTitle: existingProject.textBlockTitle,
      textBlockContent: existingProject.textBlockContent,
      resourceLinks: existingProject.resourceLinks ?? [],
      displayMixerQuestion: existingProject.displayMixerQuestion ?? false,
      mixerSurveyId: existingProject.mixerSurveyId,
      surveyIds: existingProjectResponse.surveys.map(survey => survey.surveyId), // Copy all associated surveys
    };

    // Store the new project (without projectId, so it creates a new one)
    const response = await storeProject(duplicateProjectData);

    return response;
  } catch (error) {
    loggingUtility.error('Error duplicating project:', error);
    throw error;
  }
};
